import type { MutationType, RegisterMutation } from 'generated/gql';
import { PartyType } from 'generated/gql';
import type { StepProps } from '@rulebooks/evidend-titan';
import { Logo } from '@rulebooks/evidend-titan';
import { FullscreenLayout, Link, Stepper } from '@rulebooks/evidend-titan';
import { type IWizard } from 'components/Wizard/Wizard';
import { formatMutationType } from 'helpers/formatMutation';
import { getPartyTypeUrl } from 'helpers/getFromParty';
import { PageTitle } from 'helpers/PageTitle';
import { capitalize, isFunction } from 'lodash';
import { useRouter } from 'next/router';
import React from 'react';
import { useCallback, useEffect } from 'react';
import { usePrevious } from 'react-use';

import { substepAllowed } from './helpers';

interface IWizardContent extends IWizard {
  mutation?: RegisterMutation;
  mutationType: MutationType;
  step: string;
  steps: StepProps[];
  visitStep: (step: string, replace?: boolean | undefined) => void;
}
export const WizardContent = ({
  children,
  mutation,
  mutationType,
  step,
  steps,
  visitStep,
  closeTo = '/',
  defaultStep,
  stepValidator,
}: IWizardContent) => {
  const router = useRouter();

  // Redirect to specified `closeTo` location when the FullscreenLayout is closed.
  const onClose = useCallback(() => router.push(closeTo), [router, closeTo]);

  // Callback when a Stepper item is clicked.
  const onStepChange = useCallback(
    async (newStep: string) => {
      // Ignore changing to the current step.
      if (step === newStep) return;

      // Stepper prevents moving to a step if the step before it is not yet completed.
      // It does not prevent moving to a substep if the substep before is not yet completed.
      const isSubstepAllowed = substepAllowed(steps, newStep);
      if (isSubstepAllowed) {
        visitStep(newStep);
      }
    },
    [visitStep, step, steps],
  );

  // Remember previous step.
  const previousStep = usePrevious(step);

  // Redirect invalid or missing step.
  useEffect(() => {
    // Only validate step if it changed.
    if (previousStep === step) return;

    const isStepValid = stepValidator(step, steps);
    if (isStepValid) {
      return;
    }
    // Determine the default step.
    const hasDefaultStep = typeof defaultStep === 'string';
    if (hasDefaultStep) {
      visitStep(defaultStep, true);
      return;
    }
    const hasFallbackStep = isFunction(defaultStep) && !!mutation;
    if (hasFallbackStep) {
      visitStep(defaultStep(mutation), true);
    }
  }, [stepValidator, defaultStep, step, steps, visitStep, mutation, previousStep]);

  const legalEntity = mutation?.register.legalEntity;
  const sidebar = (
    <>
      {legalEntity && (
        <Link
          spaceAfter={10}
          to={getPartyTypeUrl({
            id: legalEntity.id,
            type: PartyType.LegalEntity,
          })}
        >
          {legalEntity.name}
        </Link>
      )}
      <Stepper activeStep={step} steps={steps} onChange={onStepChange} />
    </>
  );
  const title = `${formatMutationType({
    type: mutationType,
    title: mutation?.title || '',
  })} | ${capitalize(step)}`;

  return (
    <FullscreenLayout
      isModal
      isShown
      brandName=""
      logoComponent={<Logo onClick={() => router.push('/')} />}
      sidebar={sidebar}
      onClose={onClose}
    >
      <PageTitle title={title} />
      {/*
        Intentionally a <div> and not a <form>: some wizard steps render their
        own <form> (e.g. the table search field on the share types step) and
        <form> elements may not be nested. Steps submit via their own buttons,
        and every form field already opts out of autofill (autoComplete="off"),
        so no wrapping <form> is needed.
      */}
      <div>{children}</div>
    </FullscreenLayout>
  );
};
